// composables
import { reactive, toRefs, Ref } from 'vue'
// utilities
import axios, { AxiosInstance, AxiosResponse, AxiosRequestConfig, AxiosHeaders } from 'axios'
import * as Sentry from '@sentry/vue'
import { alertToast } from '@/utilities/notification'
// stores
import { useAuthStore } from '@/stores/AuthStore'

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'

interface ApiRequestConfig {
  baseURL?: string
  endpoint: string
  method?: HttpMethod
  params?: Record<string, unknown> | null
  data?: unknown
  headers?: Record<string, string> | null
  timeoutMs?: number
}

interface ApiResponseMeta {
  pagination?: {
    total: number
    per_page: number
    current_page: number
  }
  [k: string]: unknown
}

interface ApiResponseObject<T = unknown> {
  data: T | null
  success: boolean
  errors?: string[] | null
  message?: string | null
  // legacy keys supported but marked as deprecated
  status?: string | number | undefined // DEPRECATE
  meta?: ApiResponseMeta
  http?: AxiosResponse | null
}

type ApiRequestError = {
  code: number
  message: string
  response: AxiosResponse | unknown
}

type SendFn = <T = unknown>(config: ApiRequestConfig) => Promise<ApiResponseObject<T>>

// single axios instance for this composable
const httpClient: AxiosInstance = axios.create({
  withCredentials: true,
  validateStatus: () => true // we handle all statuses ourselves
})

/**
 * Normalize varying backend response shapes into a single `ApiResponseObject` structure.
 * Handles responses that are objects or arrays, either wrapped in a data property or raw.
 *
 * @template T - Expected payload type (object or array of objects)
 * @param {AxiosResponse} res - Raw Axios response
 * @returns {ApiResponseObject<T>} Normalized response
 */
function normalizeApiPayload<T = unknown>(res: AxiosResponse): ApiResponseObject<T> {
  // Accepts a variety of backend shapes and normalizes them.
  const body = res?.data ?? null

  // common shapes we've seen:
  // 1) { data, success, errors?, message?, meta? }
  // 2) { data, response: { status: 'success'|'error', message }, errors? }
  // 3) raw array/object without wrapping { data: ... }
  const responseBlock = body?.response
  const inferredSuccess =
    body?.success === true || responseBlock?.status === 'success' || (res.status >= 200 && res.status < 300)

  const message: string | null = body?.message ?? responseBlock?.message ?? res.statusText ?? null

  const errors: string[] | null = Array.isArray(body?.errors) ? body.errors : body?.error ? [String(body.error)] : null

  // prefer body.data; otherwise fall back to body if it looks like payload
  // body.data will be an object or array; body itself may be an object or array
  const payload = body?.data !== undefined ? body.data : body

  const meta: ApiResponseMeta | undefined =
    body?.meta ?? (body?.pagination ? { pagination: body.pagination } : responseBlock?.meta)

  return {
    data: (payload ?? null) as T | null,
    success: !!inferredSuccess,
    errors: errors ?? null,
    message,
    status: responseBlock?.status, // DEPRECATE: passthrough for legacy consumers
    meta,
    http: res
  }
}

/**
 * Composable for making API requests with consistent error handling, Sentry logging,
 * and response normalization. Uses a shared Axios instance with auth header injection.
 */
export default function useApiRequest(): {
  response: Ref<ApiResponseObject | null>
  data: Ref<ApiResponseObject['data']>
  isLoading: Ref<boolean>
  requestError: Ref<string | null>
  send: SendFn
  hasErrors: () => boolean
  // non-breaking addition for convenience:
  http: Ref<AxiosResponse | null>
} {
  const state = reactive({
    response: null as ApiResponseObject | null,
    data: null as ApiResponseObject['data'],
    http: null as AxiosResponse | null,
    isLoading: false,
    requestError: null as string | null
  })

  const send = async <T = unknown>({
    baseURL = import.meta.env.VITE_API_BASE_URL,
    endpoint,
    method = 'GET',
    params = {},
    data,
    headers = {},
    timeoutMs
  }: ApiRequestConfig): Promise<ApiResponseObject<T>> => {
    if (!baseURL) {
      const msg = `baseURL is undefined for endpoint: ${endpoint}`
      state.requestError = msg
      alertToast('Error', msg, 'error')
      return Promise.reject({ hasError: true, message: msg })
    }
    if (!endpoint) {
      const msg = 'endpoint is undefined'
      state.requestError = msg
      alertToast('Error', msg, 'error')
      return Promise.reject({ hasError: true, message: msg })
    }

    state.isLoading = true
    state.requestError = null

    try {
      const cfg: AxiosRequestConfig = {
        baseURL,
        url: endpoint,
        method,
        params: params ?? undefined,
        data: data ?? undefined,
        headers: headers ?? undefined,
        timeout: timeoutMs
      }

      const res: AxiosResponse = await httpClient.request(cfg)

      // Normalize first so everything downstream uses the same shape
      const normalized = normalizeApiPayload<T>(res)
      state.response = normalized
      state.http = res
      state.data = normalized.data

      // Handle auth errors explicitly
      if (res.status === 401) {
        const authStore = useAuthStore()
        authStore.signOut()
        state.requestError = 'Session expired'
        alertToast('Session Expired', 'Sign in again', 'error')
        throw <ApiRequestError>{
          code: 401,
          message: state.requestError,
          response: res
        }
      }

      // Soft-fail for 403/404: return success=false but don’t throw
      if (res.status === 400 || res.status === 403 || res.status === 404) {
        return {
          ...normalized,
          success: false
        }
      }

      // For other non-2xx statuses, treat as error
      if (res.status < 200 || res.status >= 300 || normalized.success === false) {
        const msg = normalized.message || res.statusText || `HTTP ${res.status}`
        state.requestError = msg
        throw <ApiRequestError>{
          code: res.status,
          message: msg,
          response: res
        }
      }

      return normalized
    } catch (err: unknown) {
      // Notify user
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      const axiosError =
        err && typeof err === 'object' && 'response' in err ? (err as { response?: AxiosResponse }) : null
      const msg = errorMessage

      // Check if this is a 401 session expiration error
      const isSessionExpired =
        (err && typeof err === 'object' && 'code' in err && (err as { code?: number }).code === 401) ||
        axiosError?.response?.status === 401

      // Only show toast for non-session-expired errors (session expired toast is already shown above)
      if (!isSessionExpired) {
        alertToast('Error (API Request)', msg, 'error')
      }

      // Skip Sentry logging for session expiration (401) - this is expected behavior
      if (!isSessionExpired) {
        // Sentry context
        Sentry.captureException(err, {
          contexts: {
            requestDetails: {
              method,
              endpoint,
              params,
              headers,
              data: safeDescribeData(data)
            },
            responseDetails: {
              status: axiosError?.response?.status,
              statusText: axiosError?.response?.statusText,
              responseData: axiosError?.response?.data
            }
          }
        })
      }

      state.requestError = msg

      return Promise.reject({
        hasError: true,
        message: msg,
        http: axiosError?.response ?? null
      })
    } finally {
      state.isLoading = false
    }
  }

  const hasErrors = () =>
    !!state.requestError || (Array.isArray(state.response?.errors) && state.response!.errors!.length > 0)

  return {
    ...(toRefs(state) as {
      response: Ref<ApiResponseObject | null>
      data: Ref<ApiResponseObject['data']>
      isLoading: Ref<boolean>
      requestError: Ref<string | null>
      http: Ref<AxiosResponse | null>
    }),
    // Keep the declared outward types stable:
    send: send as SendFn,
    hasErrors
  }
}

/**
 * Safely describe data types for Sentry without leaking large payloads.
 * Returns a shape mirroring the input but with values replaced by their typeof.
 */
function safeDescribeData(input: unknown): Record<string, unknown> | string | null {
  try {
    const obj = typeof input === 'string' ? JSON.parse(input) : input
    if (!obj || typeof obj !== 'object' || obj === null) return typeof obj
    const mapTypes = (o: Record<string, unknown>): Record<string, unknown> =>
      Object.keys(o).reduce((acc: Record<string, unknown>, k: string) => {
        const v = o[k]
        acc[k] =
          v && typeof v === 'object' && v !== null && !Array.isArray(v)
            ? mapTypes(v as Record<string, unknown>)
            : typeof v
        return acc
      }, {})
    return mapTypes(obj as Record<string, unknown>)
  } catch {
    return null
  }
}
